import React from "react";
import { useRosterState } from "amazon-chime-sdk-component-library-react";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { MeetingInfoContext } from "../../../../context";
import { useGetMeetingJoinInfo } from "../../../../store";
import { MeetingJoinInfo } from "../../../../types";

/**
 * Component for selecting which attendees can see shared content in a meeting
 */
const AttendeeSelect: React.FC = () => {
  const theme = useTheme();
  const { roster } = useRosterState();
  const { otherAttendees, selectedAttendees, setSelectedAttendees, meetingId, userId, joinCode } =
    React.useContext(MeetingInfoContext);

  // Fetch user details for all attendees
  const { data } = useGetMeetingJoinInfo(meetingId, userId, joinCode)

  /**
   * Get attendee display information for a given roster ID
   */
  const getAttendeeInfo = (userId: string, index: number): MeetingJoinInfo => {
    const rosterEntry = roster[userId];
    if (!rosterEntry?.name) {
      return {
        fullName: "",
        jobTitle: "",
        joinNumber: index,
        userId,
        inMeeting: false
      };
    }

    let userType: string = `${(rosterEntry as any)?.userType}`
    if (userType) {
      userType = ` (${userType})`
    }

    const actualId = (rosterEntry as any).actualUserId || "";

    return {
      userId: actualId,
      fullName: rosterEntry.name + userType,
      jobTitle: "",
      joinNumber: index,
      inMeeting: true
    };
  };

  // Build attendee information dictionary
  const attendeeInfoMap: Record<string, MeetingJoinInfo> = React.useMemo(() => {

    const map: Record<string, MeetingJoinInfo> = {};

    if (data?.joinInfo) {
      data.joinInfo.forEach((currentUser) => {
        if (currentUser) {
          map[currentUser.userId] = currentUser;
        }
      });
    }

    const keyList = Object.keys(roster)

    for (let i = 0; i < keyList.length; i++) {
      const id = keyList[i]
      if (!map[id]) {
        map[id] = getAttendeeInfo(id, i);
      }
    }

    return map
  }, [roster, data])

  /**
   * Handle selection change in the dropdown
   */
  const handleSelectionChange = (event: SelectChangeEvent<string[]>) => {
    const newSelectedAttendees = event.target.value as string[];
    setSelectedAttendees(newSelectedAttendees);
  };

  /**
   * Generate full display name for an attendee including title and type
   */
  const getFullDisplayName = (id: string): string => {
    const info = attendeeInfoMap[id];
    if (!info?.fullName) return "";

    let displayName = info.fullName

    // // Add type in parentheses if available
    // if (info.type) {
    //   displayName += ` (${ info.type })`;
    // }

    // Prepend title in parentheses if available
    if (info.jobTitle) {
      displayName = `(${info.jobTitle}) ${displayName} `;
    }

    return displayName;
  };

  const hasSelectableAttendees = React.useMemo(() => {
    return otherAttendees.some((id) => {
      const info = attendeeInfoMap[id];
      const displayName = getFullDisplayName(id);
      return !!info?.fullName && !!displayName;
    });
  }, [otherAttendees, attendeeInfoMap]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <FormControl>
        <Select
          id="attendee-select"
          name="attendee-select"
          value={selectedAttendees}
          multiple
          sx={{ pr: 2, height: "45px" }}
          onChange={handleSelectionChange}
          disabled={!hasSelectableAttendees}
          displayEmpty
          renderValue={(selected) => (
            <Stack direction="row" spacing={1}>
              <PeopleIcon fontSize="small" />
              <Typography variant="body2" sx={{ pt: 0.3 }} color="textSecondary">
                {selected.length === 0
                  ? "No Attendees Selected"
                  : selected.length === 1
                    ? "1 Attendee Selected"
                    : `${selected.length} Attendees Selected`}
              </Typography>
            </Stack>
          )}
        >
          {otherAttendees.map((id) => {
            const info = attendeeInfoMap[id];
            const displayName = getFullDisplayName(id);

            // Skip attendees without valid info
            if (!info?.fullName || !displayName) {
              return null;
            }

            const isSelected = selectedAttendees.includes(id);

            return (
              <MenuItem
                key={id}
                value={id}
                sx={{
                  color: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.grey[600],
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {isSelected ? (
                    <VisibilityIcon fontSize="small" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" />
                  )}
                  <Typography variant="body2">{displayName}</Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
};

export default AttendeeSelect;
